import { useMutation } from '@apollo/client';
import {
    faArrowRotateRight,
    faPencil,
    faPlugCircleCheck,
    faSpinnerThird,
    faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import AlphaplanConnectionTestModal from './AlphaplanConnectionTestModal.tsx';
import {
    DELETE_ALPHAPLAN_INTEGRATION,
    GET_ALPHAPLAN_INTEGRATIONS,
    TEST_ALPHAPLAN_INTEGRATION_CONNECTION,
    TRIGGER_ALPHAPLAN_INTEGRATION_MASTER_DATA_UPDATE,
} from './queries.ts';
import {
    CAPABILITY_VERSION_KEY,
    ORDERS_RECORD_TYPE,
    parseJsonStringObject,
    type AlphaplanChannel,
    type AlphaplanConnectionTestResult,
    type AlphaplanIntegration,
} from './types.ts';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';
import { url } from '../../../core/utils/link.ts';
import { CUSTOMER_INTEGRATION_ALPHAPLAN_EDIT_PATH } from '../../constants.ts';

const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);
const RefreshIcon = withIcon(faArrowRotateRight);
const LoadingIcon = withIcon(faSpinnerThird);
const TestConnectionIcon = withIcon(faPlugCircleCheck);

const ChannelTags = ({ channels, label }: { channels: AlphaplanChannel[]; label: string }) => {
    if (channels.length === 0) return null;
    return (
        <div className="flex gap-1.5 flex-wrap items-center">
            <span className="text-content-medium text-xs">{label}:</span>
            {channels.map((channel) => (
                <span
                    key={channel.id}
                    className="border-line-weak border border-solid bg-surface-strong px-1 rounded text-xs break-words"
                >
                    {channel.name}
                </span>
            ))}
        </div>
    );
};

const MasterDataStatus = ({ integration }: { integration: AlphaplanIntegration }) => {
    const { t } = useTranslation('integrations');

    if (integration.masterDataUpdateActive) {
        return (
            <span className="flex gap-2 items-center animate-pulse">
                <span className="text-content-success-strong">
                    <LoadingIcon spin />
                </span>
                <span>
                    {`${t('alphaplan.masterDataUpdateInProgress')} ${((integration.fetchProgress ?? 0) * 100).toFixed(2)}%`}
                </span>
            </span>
        );
    }

    if (integration.masterDataUpdateError) {
        return (
            <div className="flex flex-col gap-1">
                <span className="text-content-danger-strong">{t('alphaplan.masterDataUpdateError')}</span>
                <span
                    className="text-content-medium text-xs whitespace-pre-line break-words max-w-xs line-clamp-4"
                    title={integration.masterDataUpdateError}
                >
                    {integration.masterDataUpdateError}
                </span>
            </div>
        );
    }

    if (!integration.masterDataUpdatedAt) {
        return <span className="text-content-medium">{t('alphaplan.neverUpdated')}</span>;
    }

    return (
        <span>
            {t('alphaplan.lastMasterDataUpdate', {
                updatedAt: format(new Date(integration.masterDataUpdatedAt), 'do MMMM yyyy, HH:mm'),
            })}
        </span>
    );
};

export interface AlphaplanRowProps {
    integration: AlphaplanIntegration;
    refetch: () => Promise<any>;
}

const AlphaplanRow = ({ integration, refetch }: AlphaplanRowProps) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
    const [connectionTestResult, setConnectionTestResult] = useState<AlphaplanConnectionTestResult | null>(null);

    const version = parseJsonStringObject(integration.capabilities)[CAPABILITY_VERSION_KEY];

    const [deleteIntegration] = useMutation(DELETE_ALPHAPLAN_INTEGRATION, {
        refetchQueries: [GET_ALPHAPLAN_INTEGRATIONS],
    });
    const handleDeleteIntegration = async () => {
        setIsDeleteConfirmVisible(false);
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('alphaplan.deleteSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('alphaplan.deleteError'), status: 'error' });
        }
    };

    const [updateMasterData] = useMutation(TRIGGER_ALPHAPLAN_INTEGRATION_MASTER_DATA_UPDATE);
    const handleUpdateMasterData = async (recordTypes?: string[]) => {
        try {
            const response = await updateMasterData({ variables: { id: integration.id, recordTypes } });
            const result = response.data?.triggerAlphaplanIntegrationMasterDataUpdate;
            await refetch();
            if (result && !result.started) {
                // e.g. an import is already running; the backend says why
                publishToast({ description: result.message || t('alphaplan.updateMasterDataError'), status: 'error' });
                return;
            }
            publishToast({ description: t('alphaplan.updateMasterDataSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('alphaplan.updateMasterDataError'), status: 'error' });
        }
    };

    const [testConnection, { loading: isTesting }] = useMutation(TEST_ALPHAPLAN_INTEGRATION_CONNECTION);
    const handleTestConnection = async () => {
        try {
            const response = await testConnection({ variables: { id: integration.id } });
            const result = response.data?.testAlphaplanIntegrationConnection;
            if (!result) throw new Error('empty testAlphaplanIntegrationConnection response');
            setConnectionTestResult(result);
            await refetch();
        } catch (error) {
            console.error(error);
            publishToast({ description: t('alphaplan.connectionTest.requestError'), status: 'error' });
        }
    };

    return (
        <>
            <Table.Row>
                <Table.Cell className="align-top py-3">
                    <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="font-medium">{integration.name}</span>
                        <span className="text-content-medium text-xs break-all" title={integration.baseUrl}>
                            {integration.baseUrl}
                        </span>
                        {version && (
                            <span className="text-content-medium text-xs">
                                {t('alphaplan.table.version')}: {String(version)}
                            </span>
                        )}
                    </div>
                </Table.Cell>
                <Table.Cell className="align-top py-3">
                    <div className="flex flex-col gap-1.5">
                        <ChannelTags
                            channels={integration.orderConfirmationChannels}
                            label={t('alphaplan.table.orderConfirmationChannels')}
                        />
                        <ChannelTags
                            channels={integration.invoiceChannels}
                            label={t('alphaplan.table.invoiceChannels')}
                        />
                        {integration.orderConfirmationChannels.length === 0 &&
                            integration.invoiceChannels.length === 0 && (
                                <span className="text-content-medium text-xs">{t('alphaplan.table.noChannels')}</span>
                            )}
                    </div>
                </Table.Cell>
                <Table.Cell className="align-top py-3">
                    <MasterDataStatus integration={integration} />
                </Table.Cell>
                <Table.Cell sticky="right">
                    <div className="flex gap-2">
                        <Tooltip content={t('alphaplan.testConnection')}>
                            <RowControlIconButton
                                onClick={handleTestConnection}
                                disabled={isTesting}
                                aria-label={t('alphaplan.testConnection')}
                            >
                                {isTesting ? <LoadingIcon spin /> : <TestConnectionIcon />}
                            </RowControlIconButton>
                        </Tooltip>

                        <DropdownMenu modal={false}>
                            <DropdownMenu.Trigger asChild>
                                <RowControlIconButton aria-label={t('alphaplan.triggerUpdate')}>
                                    <RefreshIcon />
                                </RowControlIconButton>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content align="end">
                                <DropdownMenu.Item onSelect={() => handleUpdateMasterData()}>
                                    {t('alphaplan.triggerFullUpdate')}
                                </DropdownMenu.Item>
                                <DropdownMenu.Item onSelect={() => handleUpdateMasterData([ORDERS_RECORD_TYPE])}>
                                    {t('alphaplan.triggerOrdersUpdate')}
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu>

                        <Tooltip content={t('alphaplan.edit')}>
                            <RowControlIconButton asChild>
                                <Link
                                    to={url(CUSTOMER_INTEGRATION_ALPHAPLAN_EDIT_PATH, {
                                        integrationId: integration.id,
                                    })}
                                    className="!text-content-strong hover:!text-content-strong"
                                    aria-label={t('alphaplan.edit')}
                                >
                                    <EditIcon />
                                </Link>
                            </RowControlIconButton>
                        </Tooltip>

                        <Tooltip content={t('alphaplan.delete')}>
                            <RowControlIconButton
                                className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                                onClick={() => setIsDeleteConfirmVisible(true)}
                                aria-label={t('alphaplan.delete')}
                            >
                                <DeleteIcon />
                            </RowControlIconButton>
                        </Tooltip>
                    </div>
                </Table.Cell>
            </Table.Row>

            {isDeleteConfirmVisible && (
                <ConfirmModal
                    danger
                    title={t('alphaplan.deleteConfirm.title')}
                    labelConfirm={t('alphaplan.deleteConfirm.confirmButton')}
                    onConfirm={handleDeleteIntegration}
                    onCancel={() => setIsDeleteConfirmVisible(false)}
                >
                    {t('alphaplan.deleteConfirm.description', { name: integration.name })}
                </ConfirmModal>
            )}

            {connectionTestResult && (
                <AlphaplanConnectionTestModal
                    integrationName={integration.name}
                    result={connectionTestResult}
                    onClose={() => setConnectionTestResult(null)}
                />
            )}
        </>
    );
};

export default AlphaplanRow;
